import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { chatRooms, type ChatChannel, type ChatMessage, quickEmojis, moderationPhrases } from "@/mocks/liveChat";
import Swal from "sweetalert2";

function timeAgo(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return "Ahora";
  if (diffMin < 60) return `Hace ${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  return `Hace ${Math.floor(diffHrs / 24)}d`;
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ── Message Component ── */
function ChatMessageBubble({
  msg,
  onReact,
  onModerate,
}: {
  msg: ChatMessage;
  onReact: (msgId: number, emoji: string) => void;
  onModerate: (msgId: number) => void;
}) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      className={`group flex items-start gap-3 px-3 py-1.5 hover:bg-background-50/50 transition-colors ${
        msg.isPinned ? 'bg-amber-50/50 border-l-2 border-amber-400' : ''
      }`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Avatar */}
      <div className="relative shrink-0">
        <div className="w-9 h-9 rounded-full overflow-hidden bg-background-100 border border-background-200">
          <img src={msg.userAvatar} alt={msg.userName} className="w-full h-full object-cover object-top" loading="lazy" />
        </div>
        {msg.isModerator && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-50 flex items-center justify-center">
            <i className="ri-shield-check-fill text-white text-[7px]"></i>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{msg.userName}</span>
          {msg.userBadge && (
            <span className="text-[10px]" title={msg.userTier}>{msg.userBadge}</span>
          )}
          {msg.isModerator && (
            <span className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[8px] font-bold">MOD</span>
          )}
          <span className={`text-[10px] font-semibold ${msg.userTierColor}`}>{msg.userTier}</span>
          <span className="text-[9px] text-foreground-400">{timeAgo(msg.timestamp)}</span>
          {msg.isPinned && (
            <span className="flex items-center gap-0.5 text-amber-500 text-[9px] font-semibold">
              <i className="ri-pushpin-fill text-[9px]"></i> Fijado
            </span>
          )}
        </div>
        <p className="text-sm text-foreground-800 mt-0.5 leading-relaxed break-words">{msg.content}</p>

        {/* Reactions */}
        {msg.reactions.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {msg.reactions.map((r, idx) => (
              <button
                key={idx}
                onClick={() => onReact(msg.id, r.emoji)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer ${
                  r.reacted
                    ? 'bg-primary-100 border border-primary-300 text-primary-700'
                    : 'bg-background-100 border border-background-200 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span>{r.emoji}</span>
                <span>{r.count}</span>
              </button>
            ))}
            <button
              onClick={() => onReact(msg.id, '❤️')}
              className="w-6 h-6 rounded-full bg-background-100 border border-background-200 flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-200 cursor-pointer opacity-0 group-hover:opacity-100 transition-all"
            >
              <i className="ri-add-line text-[10px]"></i>
            </button>
          </div>
        )}
      </div>

      {/* Action buttons (hover) */}
      {showActions && (
        <div className="flex items-center gap-0.5 shrink-0 self-center">
          <button
            onClick={() => onReact(msg.id, '❤️')}
            className="w-7 h-7 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
            title="Reaccionar"
          >
            <i className="ri-emotion-line text-xs"></i>
          </button>
          <button
            onClick={() => onModerate(msg.id)}
            className="w-7 h-7 rounded-lg bg-background-100 hover:bg-rose-100 flex items-center justify-center text-foreground-400 hover:text-rose-600 cursor-pointer"
            title="Reportar"
          >
            <i className="ri-flag-line text-xs"></i>
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Channel List Item ── */
function ChannelItem({
  channel,
  isActive,
  onClick,
}: {
  channel: ChatChannel;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all cursor-pointer ${
        isActive ? 'bg-background-200/70 text-foreground-950' : 'text-foreground-500 hover:bg-background-200/40 hover:text-foreground-700'
      }`}
    >
      <i className={`text-xs ${channel.type === 'radio' ? 'ri-radio-line' : channel.type === 'programa' ? 'ri-mic-line' : channel.type === 'evento' ? 'ri-calendar-event-line' : 'ri-trophy-line'}`}></i>
      <span className="text-[11px] font-semibold truncate flex-1">#{channel.name}</span>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="flex items-center gap-0.5 text-[9px] text-foreground-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
          {channel.onlineCount}
        </span>
        {channel.unreadCount > 0 && (
          <span className="min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1">
            {channel.unreadCount > 99 ? '99+' : channel.unreadCount}
          </span>
        )}
      </div>
    </button>
  );
}

/* ── Main Chat Page ── */
export default function AppChatPage() {
  const navigate = useNavigate();
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeRoomId, setActiveRoomId] = useState(chatRooms[0].id);
  const [activeChannelId, setActiveChannelId] = useState<string | null>(chatRooms[0].channels[0].id);
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showModPanel, setShowModPanel] = useState(false);
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>(() => {
    const map: Record<string, ChatMessage[]> = {};
    chatRooms.forEach((room) => {
      room.channels.forEach((ch) => {
        map[ch.id] = [...ch.messages];
      });
    });
    return map;
  });

  const activeRoom = useMemo(() => chatRooms.find((r) => r.id === activeRoomId), [activeRoomId]);
  const activeChannel = useMemo(
    () => activeRoom?.channels.find((ch) => ch.id === activeChannelId) || (activeRoom?.channels[0] ?? null),
    [activeRoom, activeChannelId],
  );

  const channelMessages = activeChannelId ? (messages[activeChannelId] || []) : [];

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [channelMessages]);

  const handleSendMessage = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!message.trim() || !activeChannelId) return;

      const newMsg: ChatMessage = {
        id: Date.now(),
        userId: 'me',
        userName: 'Marco Rivera',
        userAvatar: 'https://readdy.ai/api/search-image?query=Young%20Hispanic%20male%20portrait%20friendly%20warm%20smile%20casual%20modern%20style%20clean%20background%20soft%20natural%20lighting&width=80&height=80&seq=chat-user-marco&orientation=squarish',
        userTier: 'Top Fan',
        userTierColor: 'text-orange-500',
        content: message.trim(),
        timestamp: new Date().toISOString(),
        isModerator: false,
        isPinned: false,
        reactions: [],
      };

      setMessages((prev) => ({
        ...prev,
        [activeChannelId]: [...(prev[activeChannelId] || []), newMsg],
      }));
      setMessage("");
      setShowEmojiPicker(false);
    },
    [message, activeChannelId],
  );

  const handleReact = useCallback((msgId: number, emoji: string) => {
    if (!activeChannelId) return;
    setMessages((prev) => {
      const list = [...(prev[activeChannelId] || [])];
      const idx = list.findIndex((m) => m.id === msgId);
      if (idx === -1) return prev;
      const existingReaction = list[idx].reactions.find((r) => r.emoji === emoji);
      const newReactions = existingReaction
        ? list[idx].reactions.map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.reacted ? r.count - 1 : r.count + 1, reacted: !r.reacted }
              : r,
          )
        : [...list[idx].reactions, { emoji, count: 1, reacted: true }];
      list[idx] = { ...list[idx], reactions: newReactions };
      return { ...prev, [activeChannelId]: list };
    });
  }, [activeChannelId]);

  const handleModerate = useCallback((msgId: number) => {
    Swal.fire({
      title: 'Moderación',
      text: '¿Qué acción querés realizar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar mensaje',
      cancelButtonText: 'Silenciar usuario',
      showDenyButton: true,
      denyButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        // Delete message
        if (!activeChannelId) return;
        setMessages((prev) => ({
          ...prev,
          [activeChannelId]: (prev[activeChannelId] || []).filter((m) => m.id !== msgId),
        }));
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: moderationPhrases[0], showConfirmButton: false, timer: 2000, timerProgressBar: true });
      } else if (result.isDismissed) {
        Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: moderationPhrases[1], showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  }, [activeChannelId]);

  const totalOnline = useMemo(
    () => chatRooms.reduce((sum, room) => sum + room.channels.reduce((s, ch) => s + ch.onlineCount, 0), 0),
    [],
  );

  return (
    <div className="fixed inset-0 flex flex-col bg-background-50 overflow-hidden">
      {/* Top bar */}
      <div className="h-12 bg-background-50 border-b border-background-200 flex items-center px-3 shrink-0 safe-top">
        <button
          onClick={() => navigate('/app')}
          className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer mr-2 shrink-0"
        >
          <i className="ri-arrow-left-line text-foreground-600 text-sm"></i>
        </button>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer mr-3 shrink-0"
        >
          <i className={`${sidebarOpen ? 'ri-menu-fold-line' : 'ri-menu-unfold-line'} text-foreground-600 text-sm`}></i>
        </button>

        {activeChannel && (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <i className={`text-sm ${
              activeChannel.type === 'radio' ? 'text-rose-500 ri-radio-line' :
              activeChannel.type === 'programa' ? 'text-emerald-500 ri-mic-line' :
              activeChannel.type === 'evento' ? 'text-amber-500 ri-calendar-event-line' :
              'text-orange-500 ri-trophy-line'
            }`}></i>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-foreground-950 truncate flex items-center gap-1.5">
                #{activeChannel.name}
                {activeChannel.slowMode && (
                  <span className="text-[9px] font-normal text-foreground-400">· Modo lento {activeChannel.slowModeSeconds}s</span>
                )}
              </h1>
              <p className="text-[10px] text-foreground-400 truncate">{activeChannel.parentName}</p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto shrink-0">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            {formatCount(totalOnline)} online
          </span>

          {/* Moderation toggle */}
          <button
            onClick={() => setShowModPanel(!showModPanel)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              showModPanel
                ? 'bg-accent-100 text-accent-600'
                : 'bg-background-100 text-foreground-400 hover:text-foreground-600 hover:bg-background-200'
            }`}
            title="Panel de Moderación"
          >
            <i className="ri-shield-check-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? 'w-60' : 'w-0'
          } bg-background-50 border-r border-background-200 flex flex-col overflow-hidden transition-all duration-200 shrink-0`}
        >
          <div className="flex-1 overflow-y-auto scrollbar-none py-2">
            {chatRooms.map((room) => (
              <div key={room.id} className="mb-2">
                {/* Room header */}
                <button
                  onClick={() => {
                    setActiveRoomId(room.id);
                    setActiveChannelId(room.channels[0].id);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left cursor-pointer transition-all ${
                    activeRoomId === room.id ? 'text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-lg ${room.iconBg} flex items-center justify-center`}>
                    <i className={`${room.icon} text-white text-xs`}></i>
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider">{room.name}</span>
                  {activeRoomId === room.id ? (
                    <i className="ri-arrow-down-s-line text-xs ml-auto"></i>
                  ) : (
                    <i className="ri-arrow-right-s-line text-xs ml-auto"></i>
                  )}
                </button>

                {/* Channels */}
                {room.channels.map((ch) => (
                  <ChannelItem
                    key={ch.id}
                    channel={ch}
                    isActive={activeChannelId === ch.id}
                    onClick={() => setActiveChannelId(ch.id)}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Sidebar footer */}
          <div className="p-3 border-t border-background-200">
            <button
              onClick={() => navigate('/app')}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-500 hover:text-foreground-700 text-[11px] font-semibold cursor-pointer transition-all"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              Volver a la App
            </button>
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Messages */}
          <div ref={chatRef} className="flex-1 overflow-y-auto scrollbar-none py-2">
            {channelMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-chat-smile-2-line text-foreground-300 text-2xl"></i>
                </div>
                <h3 className="text-foreground-700 font-bold text-sm mb-1">
                  {activeChannel ? `#${activeChannel.name}` : 'Seleccioná un canal'}
                </h3>
                <p className="text-foreground-400 text-xs max-w-xs">
                  {activeChannel?.description || 'Este es el inicio del chat. ¡Sé el primero en escribir!'}
                </p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {channelMessages.map((msg) => (
                  <ChatMessageBubble key={msg.id} msg={msg} onReact={handleReact} onModerate={handleModerate} />
                ))}
              </div>
            )}
          </div>

          {/* Input bar */}
          {activeChannel && (
            <div className="px-3 pb-3 shrink-0">
              {activeChannel.slowMode && (
                <div className="text-[9px] text-amber-600 mb-1 px-1 flex items-center gap-1">
                  <i className="ri-timer-line text-[9px]"></i>
                  Modo lento activado — {activeChannel.slowModeSeconds}s entre mensajes
                </div>
              )}

              {/* Emoji picker */}
              {showEmojiPicker && (
                <div className="flex gap-1 mb-2 flex-wrap">
                  {quickEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => { setMessage((m) => m + emoji); setShowEmojiPicker(false); inputRef.current?.focus(); }}
                      className="w-9 h-9 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center text-lg cursor-pointer transition-all"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="w-9 h-9 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer shrink-0 transition-all"
                >
                  <i className={`${showEmojiPicker ? 'ri-close-line' : 'ri-emotion-line'} text-sm`}></i>
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Mensaje en #${activeChannel.name}...`}
                  maxLength={500}
                  className="flex-1 bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100/50 transition-all"
                />
                <button
                  type="submit"
                  disabled={!message.trim()}
                  className="w-9 h-9 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-background-50 flex items-center justify-center cursor-pointer shrink-0 transition-all"
                >
                  <i className="ri-send-plane-fill text-sm"></i>
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Moderation Panel */}
        {showModPanel && (
          <div className="w-56 bg-background-50 border-l border-background-200 flex flex-col overflow-hidden shrink-0">
            <div className="p-3 border-b border-background-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-shield-check-fill text-accent-500 text-sm"></i>
                </div>
                <div>
                  <h3 className="text-xs font-bold text-foreground-950">Moderación</h3>
                  <p className="text-[9px] text-foreground-400">
                    {activeChannel ? `#${activeChannel.name}` : 'Sin canal'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-none p-3 space-y-3">
              {/* Quick actions */}
              <div>
                <p className="text-[9px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Acciones Rápidas</p>
                <div className="space-y-1">
                  {[
                    { icon: 'ri-delete-bin-line', label: 'Limpiar chat', color: 'text-rose-500', bg: 'hover:bg-rose-50' },
                    { icon: 'ri-volume-mute-line', label: 'Modo lento ON', color: 'text-amber-500', bg: 'hover:bg-amber-50' },
                    { icon: 'ri-user-forbid-line', label: 'Banear usuario', color: 'text-red-500', bg: 'hover:bg-red-50' },
                    { icon: 'ri-pushpin-line', label: 'Fijar mensaje', color: 'text-sky-500', bg: 'hover:bg-sky-50' },
                  ].map((action) => (
                    <button
                      key={action.label}
                      onClick={() => {
                        Swal.fire({ toast: true, position: 'top-end', icon: 'info', title: `${action.label} — próximamente`, showConfirmButton: false, timer: 1500, timerProgressBar: true });
                      }}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[11px] font-semibold text-foreground-600 cursor-pointer transition-all ${action.bg}`}
                    >
                      <i className={`${action.icon} ${action.color} text-sm`}></i>
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mod log */}
              <div>
                <p className="text-[9px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Registro</p>
                <div className="space-y-1.5">
                  {moderationPhrases.slice(0, 3).map((phrase, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[10px] text-foreground-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 mt-1 shrink-0"></span>
                      <span>{phrase}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-3 border-t border-background-200">
              <div className="flex items-center gap-2 text-[10px] text-foreground-400">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span>Sistema activo</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}